import { Injectable, isDevMode } from '@angular/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { CapacitorHttp } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { RemoteHttpService } from 'src/remote-http';
import { RemoteRequest, ContentType } from 'src/remote-request';
import { Custom_RequestMethod } from 'src/providers/common-service';
import { VolatileStorage } from 'src/storage/volatile-storage';
import { Preference } from 'src/storage/preference';
import * as globals from 'src/globals';
import { getDeviceBucket } from 'src/utils/device-bucket.util';
import { isPresent } from 'src/providers/common-service';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as appProp from 'src/appProperties';
import { ModalController, AlertController, Platform } from '@ionic/angular';
import { LoadingController } from '@ionic/angular';

import { SplashScreen as splashScreen } from '@capacitor/splash-screen';
import * as global from 'globals';
import { mygolfUtils } from 'src/custom/mygolf2u-utils';

import { App as CapacitorApp } from '@capacitor/app';
import { CommonPullupMenuComponent } from 'src/app/pages/modal-screens/common-pullup-menu/common-pullup-menu.component';

/**
 * Update metadata from version.json
 */
export interface UpdateMetadata {
  appType: string;
  environment: string;
  version: string;
  rollout: number;
  mandatory: boolean;
  sha256: string;
  disabled: boolean;
  minSafeVersion?: string;
  releaseNotes?: string;
  releasedAt?: string;
  bundleSize?: number;
}

/**
 * Response from token generation endpoint
 */
export interface UpdateTokenResponse {
  token: string;
  expiresAt: number;
}

/**
 * Live update service for multi-tenant myGolf2u app
 * Handles secure, self-hosted live updates with staged rollouts,
 * mandatory updates, kill switch, and rollback scenarios
 */
@Injectable({
  providedIn: 'root',
})
export class LiveUpdateService {
  private readonly UPDATE_BASE_URL: string;
  private readonly VERSION_URL: string;
  private readonly TOKEN_ENDPOINT: string;
  private readonly DOWNLOAD_ENDPOINT: string;
  private readonly STATIC_BUNDLE_ENDPOINT: string;

  // Storage keys
  private readonly LAST_CHECK_KEY = 'update_last_check';
  private readonly LAST_VERSION_KEY = 'update_last_version';
  private readonly UPDATE_DISABLED_KEY = 'update_disabled';

  // VolatileStorage instance
  private volatileStorage: VolatileStorage;

  environment: string = 'prod';
  constructor(
    private httpService: RemoteHttpService,
    private requestMethod: Custom_RequestMethod,
    private preference: Preference,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private mg2uUtil: mygolfUtils,
    private platform: Platform
  ) {
    if (!this.platform.is('capacitor')) return;
    // Instantiate VolatileStorage
    this.volatileStorage = new VolatileStorage();

    // Build update server URLs based on current environment
    // const env = this.environment;
    if (globals.MygolfServerHost?.toLowerCase()?.includes('prdlet')) {
      this.environment = 'prod';
    } else if (globals.MygolfServerHost?.toLowerCase()?.includes('betalet')) {
      this.environment = 'beta';
    } else if (globals.MygolfServerHost?.toLowerCase()?.includes('miglet')) {
      this.environment = 'migrate';
    }

    const updateHost = `${this.getUpdateHost(this.environment)}/api/v1`;

    // Check if running in development mode (localhost)
    const isDevMode = false;
    // window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isDevMode) {
      // Use relative URLs for proxy during development
      console.log('[LiveUpdate] Development mode detected, using proxy URLs');
      this.UPDATE_BASE_URL = '/api/v1';
      this.VERSION_URL = `${this.UPDATE_BASE_URL}/versions/${globals.appType}/${this.environment}`;
      this.TOKEN_ENDPOINT = `${this.UPDATE_BASE_URL}/auth/token`;
      this.DOWNLOAD_ENDPOINT = `${this.UPDATE_BASE_URL}/bundles/download`;
      this.STATIC_BUNDLE_ENDPOINT = '/bundles';
    } else {
      // Use full URLs in production
      this.UPDATE_BASE_URL = `https://${updateHost}`;
      this.VERSION_URL = `${this.UPDATE_BASE_URL}/versions/${globals.appType}/${this.environment}`;
      this.TOKEN_ENDPOINT = `${this.UPDATE_BASE_URL}/auth/token`;
      this.DOWNLOAD_ENDPOINT = `${this.UPDATE_BASE_URL}/bundles/download`;
      this.STATIC_BUNDLE_ENDPOINT = `${this.getUpdateHost(this.environment)}/bundles`;
    }

    // Add diagnostic logging
    console.log('[LiveUpdate] DEBUG: Initializing with configuration:');
    console.log('[LiveUpdate] DEBUG: Environment:', this.environment);
    console.log('[LiveUpdate] DEBUG: Is dev mode:', isDevMode);
    console.log('[LiveUpdate] DEBUG: Update host:', updateHost);
    console.log('[LiveUpdate] DEBUG: UPDATE_BASE_URL:', this.UPDATE_BASE_URL);
    console.log('[LiveUpdate] DEBUG: VERSION_URL:', this.VERSION_URL);
    console.log('[LiveUpdate] DEBUG: TOKEN_ENDPOINT:', this.TOKEN_ENDPOINT);
    console.log('[LiveUpdate] DEBUG: DOWNLOAD_ENDPOINT:', this.DOWNLOAD_ENDPOINT);
    console.log('[LiveUpdate] DEBUG: appType:', globals.appType);
    console.log('[LiveUpdate] DEBUG: useStaticServer:', appProp.updateConfig.useStaticServer);

    const current = CapacitorUpdater.current();
    console.log(
      '[LiveUpdate] DEBUG: capUpdater.current() :',
      JSON.stringify(CapacitorUpdater.current())
    );
    const update = this.fetchVersionMetadata();
    console.log(
      '[LiveUpdate] DEBUG: fetchVersionMetadata() :',
      JSON.stringify(this.fetchVersionMetadata())
    );
    console.log('[LiveUpdate] DEBUG: appProperties :', JSON.stringify(appProp));
  }

  /**
   * Initialize the service (call after construction if needed)
   * Async initialization moved out of constructor
   */
  async init(): Promise<void> {
    try {
      const current = await CapacitorUpdater.current();
      console.log('[LiveUpdate] DEBUG: Current bundle:', current.bundle);
    } catch (e) {
      console.warn('[LiveUpdate] Could not get current bundle:', e);
    }
  }

  /**
   * Get update server host based on environment
   * Maps environments to update server hosts
   */
  private getUpdateHost(environment: string): string {
    console.debug('[liveupdate] get environment : ', environment);
    switch (environment) {
      case 'prod':
        return 'prdupdates.mygolf2u.com';
      case 'dev':
        return 'migupdates.mygolf2u.com';
      case 'mig':
        return 'migupdates.mygolf2u.com';
      case 'staging':
        return 'betaupdates.mygolf2u.com';
      case 'beta':
        return 'betaupdates.mygolf2u.com';
      case 'prod-dev':
        return 'prdupdates.mygolf2u.com';
      case 'beta-dev':
        return 'betaupdates.mygolf2u.com';
      default:
        return 'prdupdates.mygolf2u.com';
    }
  }

  /**
   * Check for and apply updates
   * @param forceUpdate Force update check regardless of last check time
   */
  async checkAndApplyUpdate(forceUpdate: boolean = false): Promise<void> {
    console.log('[LiveUpdate] === checkAndApplyUpdate START ===');
    console.log('[LiveUpdate] forceUpdate:', forceUpdate);
    console.log('[LiveUpdate] updateConfig.enabled:', appProp.updateConfig.enabled);

    try {
      // CRITICAL: Check if updates are enabled in config BEFORE doing anything else
      if (!appProp.updateConfig.enabled && !forceUpdate) {
        console.log(
          '[LiveUpdate] ✅ Updates are DISABLED in appProperties.ts, skipping update check'
        );
        return;
      }

      console.log(
        '[LiveUpdate] ⚠️ Updates are ENABLED or forceUpdate=true, proceeding with update check'
      );

      // Check if updates are disabled
      const updatesDisabled = this.volatileStorage.getBoolean(this.UPDATE_DISABLED_KEY);
      if (updatesDisabled && !forceUpdate) {
        console.log('[LiveUpdate] Updates are disabled in VolatileStorage');
        return;
      }

      // Check last update check time (throttle to once per hour)
      if (!forceUpdate) {
        const lastCheck = this.volatileStorage.getString(this.LAST_CHECK_KEY);
        if (lastCheck) {
          const lastCheckTime = parseInt(lastCheck, 10);
          const now = Date.now();
          const oneHour = appProp.updateConfig.checkInterval; // 60 * 60 * 1000;
          if (now - lastCheckTime < oneHour) {
            console.log('[LiveUpdate] Update check throttled (checked within last hour)');
            return;
          }
        }
      }

      // Update last check time
      this.volatileStorage.setString(this.LAST_CHECK_KEY, Date.now().toString());

      // Fetch version metadata
      const update = await this.fetchVersionMetadata();

      // Check if update is disabled (kill switch)
      if (update.disabled) {
        console.log('[LiveUpdate] Update disabled, checking for rollback');
        await this.handleDisabledUpdate(update);
        return;
      }

      // Check rollout eligibility
      const bucket = await getDeviceBucket();
      if (bucket >= update.rollout) {
        console.log(
          `[LiveUpdate] Device bucket ${bucket} not eligible for rollout (${update.rollout}%)`
        );
        return;
      }

      console.log(`[LiveUpdate] Device bucket ${bucket} eligible for rollout (${update.rollout}%)`);

      // Get current version
      const current = await CapacitorUpdater.current();

      // Check if already on latest version
      if (current.bundle.version === update.version) {
        console.log('[LiveUpdate] Already on latest version:', update.version);
        return;
      } else if (current.bundle.version === 'builtin') {
        await this.downloadAndApplyUpdate(update, true);
      }

      // Check if mandatory update
      if (update.mandatory) {
        console.log('[LiveUpdate] Mandatory update required:', update.version);
        await this.downloadAndApplyUpdate(update, true);
        return;
      }

      // Optional update - check user preference
      const autoUpdateEnabled = await this.preference.getPref('autoUpdateEnabled').toPromise();
      console.log(
        `[LiveUpdate] current vs bundle`,
        JSON.stringify(current),
        JSON.stringify(update),
        autoUpdateEnabled
      );
      if (autoUpdateEnabled !== false) {
        console.log('[LiveUpdate] Auto-update enabled, proceeding with update');
        await this.downloadAndApplyUpdate(update, false);
      } else {
        console.log('[LiveUpdate] Auto-update disabled, update available but not applied');
        await this.notifyUpdateAvailable(update);
      }
    } catch (err) {
      console.warn('[LiveUpdate] Update check failed:', err);
      // Don't throw - allow app to continue
    }
  }

  /**
   * Fetch version metadata from server
   */
  private async fetchVersionMetadata(): Promise<UpdateMetadata> {
    console.log('[LiveUpdate] DEBUG: === FETCH VERSION METADATA START ===');
    console.log('[LiveUpdate] DEBUG: Attempting to fetch version metadata from:', this.VERSION_URL);
    console.log('[LiveUpdate] DEBUG: With headers:', {
      'X-App-Type': globals.appType,
      'X-Environment': this.environment,
    });

    // DIAGNOSTIC: Log current page origin for CORS debugging
    console.log('[LiveUpdate] DIAGNOSTIC: Current page origin:', window.location.origin);
    console.log('[LiveUpdate] DIAGNOSTIC: Full URL being fetched:', this.VERSION_URL);
    console.log('[LiveUpdate] DIAGNOSTIC: Update enabled in config:', appProp.updateConfig.enabled);
    console.log('[LiveUpdate] DIAGNOSTIC: Navigator online:', navigator.onLine);
    console.log('[LiveUpdate] DIAGNOSTIC: User agent:', navigator.userAgent);

    try {
      // Check if running on Android/iOS and use CapacitorHttp to bypass CORS
      const platformInfo = await Device.getInfo();

      console.log('[LiveUpdate] DEBUG: === PLATFORM DETECTION ===');
      console.log('[LiveUpdate] DEBUG: Platform info:', platformInfo);
      console.log('[LiveUpdate] DEBUG: Platform info.platform:', platformInfo.platform);
      console.log('[LiveUpdate] DEBUG: Platform info.platform type:', typeof platformInfo.platform);
      console.log('[LiveUpdate] DEBUG: Is android?:', platformInfo.platform === 'android');
      console.log('[LiveUpdate] DEBUG: Is ios?:', platformInfo.platform === 'ios');
      console.log('[LiveUpdate] DEBUG: Is web?:', platformInfo.platform === 'web');

      const isMobile = platformInfo.platform === 'android' || platformInfo.platform === 'ios';

      console.log('[LiveUpdate] DEBUG: Is mobile:', isMobile);
      console.log('[LiveUpdate] DEBUG: Will use CapacitorHttp:', isMobile);
      console.log('[LiveUpdate] DEBUG: Will use native fetch:', !isMobile);

      if (isMobile) {
        console.log('[LiveUpdate] DEBUG: Using CapacitorHttp for mobile platform to bypass CORS');

        const requestParams = {
          url: this.VERSION_URL,
          headers: {
            'X-App-Type': globals.appType,
            'X-Environment': this.environment,
            'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
          },
        };

        console.log(
          '[LiveUpdate] DEBUG: CapacitorHttp request params:',
          JSON.stringify(requestParams, null, 2)
        );

        // Use CapacitorHttp to bypass WebView CORS restrictions
        let response;
        try {
          console.log('[LiveUpdate] DEBUG: About to call CapacitorHttp.get()...');
          response = await CapacitorHttp.get(requestParams);
          console.log('[LiveUpdate] DEBUG: CapacitorHttp.get() succeeded');
        } catch (capacitorHttpError) {
          console.error(
            '[LiveUpdate] DEBUG: CapacitorHttp.get() FAILED with exception:',
            capacitorHttpError
          );
          console.error('[LiveUpdate] DEBUG: CapacitorHttp error type:', typeof capacitorHttpError);
          console.error('[LiveUpdate] DEBUG: CapacitorHttp error name:', capacitorHttpError.name);
          console.error(
            '[LiveUpdate] DEBUG: CapacitorHttp error message:',
            capacitorHttpError.message
          );
          if (capacitorHttpError.stack) {
            console.error(
              '[LiveUpdate] DEBUG: CapacitorHttp error stack:',
              capacitorHttpError.stack
            );
          }
          throw capacitorHttpError;
        }

        console.log('[LiveUpdate] DEBUG: CapacitorHttp response status:', response.status);
        console.log('[LiveUpdate] DEBUG: CapacitorHttp response data:', response.data);

        if (response.status !== 200) {
          console.error('[LiveUpdate] DEBUG: === FETCH VERSION METADATA FAILED ===');
          console.error('[LiveUpdate] DEBUG: Failed to fetch version metadata:', {
            url: this.VERSION_URL,
            status: response.status,
            appType: globals.appType,
            environment: this.environment,
          });
          throw new Error(`Failed to fetch version metadata: HTTP ${response.status}`);
        }

        const metadata: UpdateMetadata = response.data as UpdateMetadata;
        console.log('[LiveUpdate] DEBUG: === FETCH VERSION METADATA SUCCESS ===');
        console.log('[LiveUpdate] DEBUG: Successfully fetched metadata:', metadata);

        // Validate metadata structure
        if (!isPresent(metadata.version) || !isPresent(metadata.sha256)) {
          console.error('[LiveUpdate] DEBUG: === VERSION METADATA VALIDATION FAILED ===');
          console.error('[LiveUpdate] DEBUG: Invalid version metadata structure:', metadata);
          throw new Error('Invalid version metadata structure');
        }

        console.log('[LiveUpdate] DEBUG: Version metadata validation passed');
        return metadata;
      } else {
        // Web browser - use native fetch
        console.log('[LiveUpdate] DEBUG: Using native fetch for web platform');

        // DIAGNOSTIC: Test if the live update server is accessible
        console.log('[LiveUpdate] DIAGNOSTIC: Testing live update server connectivity...');
        try {
          const serverTest = await fetch('https://prdupdates.mygolf2u.com/health', {
            method: 'GET',
            cache: 'no-store',
          });
          console.log('[LiveUpdate] DIAGNOSTIC: Server health check status:', serverTest.status);
          if (serverTest.ok) {
            const healthData = await serverTest.json();
            console.log('[LiveUpdate] DIAGNOSTIC: Server health check response:', healthData);
          }
        } catch (serverError) {
          console.error('[LiveUpdate] DIAGNOSTIC: Server health check FAILED:', serverError);
          console.error('[LiveUpdate] DIAGNOSTIC: Server may be down or unreachable');
        }

        // DIAGNOSTIC: Test network connectivity first with a simple request
        console.log('[LiveUpdate] DIAGNOSTIC: Testing network connectivity...');
        try {
          const connectivityTest = await fetch('https://www.google.com/', {
            method: 'HEAD',
            cache: 'no-store',
            mode: 'no-cors',
          });
          console.log('[LiveUpdate] DIAGNOSTIC: Network connectivity test passed');
        } catch (networkError) {
          console.error('[LiveUpdate] DIAGNOSTIC: Network connectivity test FAILED:', networkError);
          console.error('[LiveUpdate] DIAGNOSTIC: Device may not have internet access');
        }

        // First, let's test if the URL is accessible with a simple HEAD request
        console.log('[LiveUpdate] DEBUG: Testing URL accessibility with HEAD request...');
        const headResponse = await fetch(this.VERSION_URL, {
          method: 'HEAD',
          cache: 'no-store',
          headers: {
            'X-App-Type': globals.appType,
            'X-Environment': this.environment,
          },
        });

        console.log('[LiveUpdate] DEBUG: HEAD response status:', headResponse.status);
        console.log(
          '[LiveUpdate] DEBUG: HEAD response headers:',
          Object.fromEntries(headResponse.headers.entries())
        );

        // Now make the actual GET request
        console.log('[LiveUpdate] DEBUG: Making GET request for version metadata...');
        const response = await fetch(this.VERSION_URL, {
          cache: 'no-store',
          headers: {
            'X-App-Type': globals.appType,
            'X-Environment': this.environment,
            'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
          },
        });

        console.log('[LiveUpdate] DEBUG: GET response status:', response.status);
        console.log('[LiveUpdate] DEBUG: GET response status text:', response.statusText);
        console.log(
          '[LiveUpdate] DEBUG: GET response headers:',
          Object.fromEntries(response.headers.entries())
        );

        if (!response.ok) {
          console.error('[LiveUpdate] DEBUG: === FETCH VERSION METADATA FAILED ===');
          console.error('[LiveUpdate] DEBUG: Failed to fetch version metadata:', {
            url: this.VERSION_URL,
            status: response.status,
            statusText: response.statusText,
            appType: globals.appType,
            environment: this.environment,
            responseHeaders: Object.fromEntries(response.headers.entries()),
          });

          // Try to get more error details if possible
          try {
            const errorText = await response.text();
            console.error('[LiveUpdate] DEBUG: Error response body:', errorText);
          } catch (e) {
            console.error('[LiveUpdate] DEBUG: Could not read error response body:', e);
          }

          throw new Error(
            `Failed to fetch version metadata: ${response.status} - ${response.statusText}`
          );
        }

        const metadata: UpdateMetadata = await response.json();
        console.log('[LiveUpdate] DEBUG: === FETCH VERSION METADATA SUCCESS ===');
        console.log('[LiveUpdate] DEBUG: Successfully fetched metadata:', metadata);

        // Validate metadata structure
        if (!isPresent(metadata.version) || !isPresent(metadata.sha256)) {
          console.error('[LiveUpdate] DEBUG: === VERSION METADATA VALIDATION FAILED ===');
          console.error('[LiveUpdate] DEBUG: Invalid version metadata structure:', metadata);
          throw new Error('Invalid version metadata structure');
        }

        console.log('[LiveUpdate] DEBUG: Version metadata validation passed');
        return metadata;
      }
    } catch (error) {
      console.error('[LiveUpdate] DEBUG: === FETCH VERSION METADATA ERROR ===');
      console.error(
        '[LiveUpdate] DEBUG: Exception in fetchVersionMetadata:',
        this.UPDATE_BASE_URL,
        this.VERSION_URL,
        globals.appType,
        this.environment
      );
      console.error('[LiveUpdate] DEBUG: Exception in fetchVersionMetadata:', error);
      console.error('[LiveUpdate] DEBUG: Error type:', typeof error);
      console.error('[LiveUpdate] DEBUG: Error message:', error.message);
      if (error.stack) {
        console.error('[LiveUpdate] DEBUG: Error stack:', error.stack);
      }

      // DIAGNOSTIC: Additional error analysis
      console.error('[LiveUpdate] DIAGNOSTIC: === ERROR ANALYSIS ===');
      console.error('[LiveUpdate] DIAGNOSTIC: Error name:', error.name);
      console.error('[LiveUpdate] DIAGNOSTIC: Is TypeError?', error instanceof TypeError);
      console.error(
        '[LiveUpdate] DIAGNOSTIC: Is NetworkError?',
        error.message?.toLowerCase().includes('network')
      );
      console.error(
        '[LiveUpdate] DIAGNOSTIC: Is CORS error?',
        error.message?.toLowerCase().includes('cors')
      );

      // DIAGNOSTIC: Check if this is a CORS-related error
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('[LiveUpdate] DIAGNOSTIC: === LIKELY CORS OR NETWORK ERROR ===');
        console.error('[LiveUpdate] DIAGNOSTIC: "Failed to fetch" typically indicates:');
        console.error(
          '[LiveUpdate] DIAGNOSTIC: 1. CORS policy violation (server not allowing requests from this origin)'
        );
        console.error('[LiveUpdate] DIAGNOSTIC: 2. Network connectivity issue');
        console.error('[LiveUpdate] DIAGNOSTIC: 3. SSL/TLS certificate issue');
        console.error('[LiveUpdate] DIAGNOSTIC: 4. Server is down or unreachable');
        console.error('[LiveUpdate] DIAGNOSTIC: Current origin:', window.location.origin);
        console.error('[LiveUpdate] DIAGNOSTIC: Target URL:', this.VERSION_URL);
        console.error(
          '[LiveUpdate] DIAGNOSTIC: Cross-origin request?',
          !this.VERSION_URL.includes(window.location.hostname)
        );
      }

      throw error;
    }
  }

  /**
   * Handle disabled update (rollback scenario)
   */
  private async handleDisabledUpdate(update: UpdateMetadata): Promise<void> {
    const current = await CapacitorUpdater.current();

    if (update.minSafeVersion && current.bundle.version !== update.minSafeVersion) {
      console.log(`[LiveUpdate] Rolling back to safe version: ${update.minSafeVersion}`);
      await CapacitorUpdater.reset();
      // Note: For rollback, we need to download the safe version first, then set it
      // This is a simplified rollback - in production, you might want to download the specific version
      this.volatileStorage.setString(this.LAST_VERSION_KEY, update.minSafeVersion);
    }
  }

  /**
   * Download and apply update
   */
  private async downloadAndApplyUpdate(update: UpdateMetadata, mandatory: boolean): Promise<void> {
    console.log('[LiveUpdate] DEBUG: === DOWNLOAD AND APPLY UPDATE START ===');

    // Token-based authentication mode
    const platformInfo = await Device.getInfo();
    const platform = platformInfo.platform; // 'ios' or 'android'
    console.log('[LiveUpdate] DEBUG: Using token-based authentication mode');
    console.log('[LiveUpdate] DEBUG: Platform:', platform);

    // Request update token
    const tokenResponse = await this.requestUpdateToken(platform);
    const token = tokenResponse.token;
    // Build secure download URL
    let downloadUrl: string;
    downloadUrl = `${this.DOWNLOAD_ENDPOINT}/${globals.appType}/${this.environment}?version=${update.version}&token=${token}`;
    console.log('[LiveUpdate] DEBUG: Final download URL:', downloadUrl);

    try {
      // Check if using static server or token-based authentication
      if (appProp.updateConfig.useStaticServer) {
        // Static server mode: Direct download without token
        // FastAPI serves bundles from /bundles/{app_type}/{environment}/
        downloadUrl = `${this.STATIC_BUNDLE_ENDPOINT}/${globals.appType}/${this.environment}/mygolf2u-${globals.appType}-${update.version}.zip`;
        console.log('[LiveUpdate] DEBUG: Using static server mode');
        console.log('[LiveUpdate] DEBUG: STATIC_BUNDLE_ENDPOINT:', this.STATIC_BUNDLE_ENDPOINT);
        console.log('[LiveUpdate] DEBUG: appType:', globals.appType);
        console.log('[LiveUpdate] DEBUG: environment:', this.environment);
        console.log('[LiveUpdate] DEBUG: version:', update.version);
        console.log('[LiveUpdate] DEBUG: Final download URL:', downloadUrl);
      } else {
        console.log('[LiveUpdate] DEBUG: Using token-based authentication mode');
        console.log('[LiveUpdate] DEBUG: Platform:', platform);

        // NOTE: Token already requested at line 541, don't request again
        // Build secure download URL using existing token
        downloadUrl = `${this.DOWNLOAD_ENDPOINT}/${globals.appType}/${this.environment}?version=${update.version}&token=${token}`;
        console.log('[LiveUpdate] DEBUG: Final download URL:', downloadUrl);
      }
      // downloadUrl =
      //   'https://prdupdates.mygolf2u.com/bundles/main/prod/mygolf2u-main-0.0.2-android.zip';

      console.log('[LiveUpdate] DEBUG: === TESTING DOWNLOAD URL ACCESSIBILITY ===');

      // Get platform info to determine which HTTP method to use
      const platformInfo = await Device.getInfo();
      const isMobile = platformInfo.platform === 'android' || platformInfo.platform === 'ios';

      console.log('[LiveUpdate] DIAGNOSTIC: Platform for HEAD test:', platformInfo.platform);
      console.log('[LiveUpdate] DIAGNOSTIC: Is mobile platform:', isMobile);
      console.log('[LiveUpdate] DIAGNOSTIC: Will use CapacitorHttp for HEAD test:', isMobile);

      // NOTE: HEAD request test removed - CapacitorHttp.request() with method: 'HEAD' is not properly supported
      // The download URL accessibility will be tested via GET request below instead (line 720+)
      console.log(
        '[LiveUpdate] DIAGNOSTIC: Skipping HEAD request test (not supported by CapacitorHttp)'
      );
      console.log(
        '[LiveUpdate] DIAGNOSTIC: URL accessibility will be tested via GET request below'
      );

      // Partial GET test and CapacitorHttp test skipped to avoid consuming token

      // Test SSL certificate details
      try {
        console.log('[LiveUpdate] DEBUG: Testing SSL certificate details...');
        const platformInfo = await Device.getInfo();
        const isMobile = platformInfo.platform === 'android' || platformInfo.platform === 'ios';

        if (isMobile) {
          // Test with a simple HTTPS request to see if SSL works
          const sslTestResponse = await CapacitorHttp.get({
            url: 'https://www.google.com',
            headers: {
              'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
            },
          });

          console.log('[LiveUpdate] DEBUG: SSL test to google.com status:', sslTestResponse.status);

          if (sslTestResponse.status === 200) {
            console.log('[LiveUpdate] DEBUG: SSL connections work with CapacitorHttp');
          } else {
            console.warn('[LiveUpdate] DEBUG: SSL test failed, status:', sslTestResponse.status);
          }
        }
      } catch (sslError) {
        console.error('[LiveUpdate] DEBUG: SSL test failed:', sslError);
      }

      // Test if CapacitorHttp can access the URL (mobile-specific test)
      try {
        console.log('[LiveUpdate] DEBUG: Testing download URL with CapacitorHttp GET...');
        const platformInfo = await Device.getInfo();
        const isMobile = platformInfo.platform === 'android' || platformInfo.platform === 'ios';

        if (isMobile) {
          const capacitorGetResponse = await CapacitorHttp.get({
            url: downloadUrl,
            headers: {
              'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
            },
            responseType: 'arraybuffer',
          });

          console.log(
            '[LiveUpdate] DEBUG: CapacitorHttp GET response status:',
            capacitorGetResponse.status
          );
          console.log(
            '[LiveUpdate] DEBUG: CapacitorHttp GET response data type:',
            typeof capacitorGetResponse.data
          );
          console.log(
            '[LiveUpdate] DEBUG: CapacitorHttp GET response data length:',
            capacitorGetResponse.data?.length
          );

          if (capacitorGetResponse.status === 200) {
            console.log(
              '[LiveUpdate] DEBUG: Download URL is accessible via CapacitorHttp GET request'
            );

            // Test if we can convert the data
            try {
              const binaryString = atob(capacitorGetResponse.data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const arrayBuffer = bytes.buffer;
              console.log(
                '[LiveUpdate] DEBUG: CapacitorHttp data conversion successful, size:',
                arrayBuffer.byteLength
              );
            } catch (conversionError) {
              console.error(
                '[LiveUpdate] DEBUG: CapacitorHttp data conversion failed:',
                conversionError
              );
            }
          } else {
            console.warn(
              '[LiveUpdate] DEBUG: CapacitorHttp GET request failed:',
              capacitorGetResponse.status
            );
          }
        } else {
          console.log('[LiveUpdate] DEBUG: Skipping CapacitorHttp test on web platform');
        }
      } catch (capacitorGetError) {
        console.error(
          '[LiveUpdate] DEBUG: CapacitorHttp GET request to download URL failed:',
          capacitorGetError
        );
        console.error('[LiveUpdate] DEBUG: Error details:', {
          name: capacitorGetError.name,
          message: capacitorGetError.message,
          stack: capacitorGetError.stack,
        });
      }

      console.log('[LiveUpdate] DEBUG: === STARTING CAPACITOR UPDATER DOWNLOAD ===');
      console.log('[LiveUpdate] DEBUG: Downloading update from:', downloadUrl);
      console.log('[LiveUpdate] DEBUG: Update version:', update.version);
      console.log('[LiveUpdate] DEBUG: Update checksum:', update.sha256);

      // Get device info for better debugging
      try {
        const deviceInfo = await Device.getInfo();
        console.log('[LiveUpdate] DEBUG: Device info:', {
          platform: deviceInfo.platform,
          operatingSystem: deviceInfo.operatingSystem,
          osVersion: deviceInfo.osVersion,
          model: deviceInfo.model,
          isVirtual: deviceInfo.isVirtual,
        });
      } catch (deviceError) {
        console.warn('[LiveUpdate] DEBUG: Could not get device info:', deviceError);
      }

      // Check current CapacitorUpdater version
      try {
        console.log('[LiveUpdate] DEBUG: About to call CapacitorUpdater.download with:', {
          url: downloadUrl,
          version: update.version,
          //checksum: update.sha256,
        });
      } catch (logError) {
        console.warn('[LiveUpdate] DEBUG: Could not log CapacitorUpdater params:', logError);
      }

      // Download update with checksum verification
      let bundle;
      try {
        console.log('[LiveUpdate] DEBUG: Attempting CapacitorUpdater.download()...');
        bundle = await CapacitorUpdater.download({
          url: downloadUrl, //'https://prdupdates.mygolf2u.com/bundles/main/prod/mygolf2u-main-0.0.2-android.zip', //downloadUrl,
          version: update.version,
          //checksum: update.sha256,
        });
        console.log('[LiveUpdate] DEBUG: CapacitorUpdater.download() succeeded');
      } catch (capacitorError) {
        console.error(
          '[LiveUpdate] DEBUG: CapacitorUpdater.download() failed, implementing complete bypass...'
        );
        console.error('[LiveUpdate] DEBUG: CapacitorUpdater error:', capacitorError);

        // Complete bypass: Manual download and direct file handling
        try {
          console.log('[LiveUpdate] DEBUG: Attempting complete bypass with Filesystem API...');
          console.log('[LiveUpdate] DEBUG: Download URL:', downloadUrl);

          // Import Filesystem dynamically
          const { Filesystem, Directory } = await import('@capacitor/filesystem');

          // Step 1: Download the file manually using CapacitorHttp for mobile, fetch for web
          let arrayBuffer: ArrayBuffer;
          const platformInfo = await Device.getInfo();
          const isMobile = platformInfo.platform === 'android' || platformInfo.platform === 'ios';

          console.log('[LiveUpdate] DEBUG: Platform:', platformInfo.platform);
          console.log('[LiveUpdate] DEBUG: Is mobile:', isMobile);

          if (isMobile) {
            console.log('[LiveUpdate] DEBUG: Using CapacitorHttp for binary download');
            console.log('[LiveUpdate] DEBUG: Request URL:', downloadUrl);

            const requestParams: any = {
              url: downloadUrl,
              headers: {
                'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
              },
              responseType: 'arraybuffer',
            };

            console.log(
              '[LiveUpdate] DEBUG: CapacitorHttp request params:',
              JSON.stringify(requestParams, null, 2)
            );

            let response;
            try {
              console.log('[LiveUpdate] DEBUG: About to call CapacitorHttp.get()...');
              response = await CapacitorHttp.get(requestParams);
              console.log('[LiveUpdate] DEBUG: CapacitorHttp.get() succeeded');
              console.log('[LiveUpdate] DEBUG: Response status:', response.status);
              console.log('[LiveUpdate] DEBUG: Response data type:', typeof response.data);
              console.log('[LiveUpdate] DEBUG: Response data length:', response.data?.length);
            } catch (capacitorHttpError) {
              console.error('[LiveUpdate] DEBUG: CapacitorHttp.get() FAILED:', capacitorHttpError);
              console.error('[LiveUpdate] DEBUG: Error name:', capacitorHttpError.name);
              console.error('[LiveUpdate] DEBUG: Error message:', capacitorHttpError.message);
              throw capacitorHttpError;
            }

            if (response.status !== 200) {
              throw new Error(`Manual download failed: ${response.status}`);
            }

            // CapacitorHttp with responseType: 'arraybuffer' returns data as base64 string
            // Convert base64 to ArrayBuffer
            console.log('[LiveUpdate] DEBUG: Converting base64 to ArrayBuffer...');
            const binaryString = atob(response.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            arrayBuffer = bytes.buffer;
            console.log('[LiveUpdate] DEBUG: ArrayBuffer created, size:', arrayBuffer.byteLength);
          } else {
            console.log('[LiveUpdate] DEBUG: Using fetch for binary download (web)');
            const response = await fetch(downloadUrl, {
              method: 'GET',
              headers: {
                'User-Agent': 'myGolf2u/5.0.0 (Capacitor)',
              },
            });

            if (!response.ok) {
              throw new Error(`Manual download failed: ${response.status} ${response.statusText}`);
            }

            arrayBuffer = await response.arrayBuffer();
          }
          const fileSize = arrayBuffer.byteLength;
          console.log('[LiveUpdate] DEBUG: Manual download succeeded, file size:', fileSize);

          // Step 2: Convert to base64 for Filesystem API
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64Data = btoa(binary);

          // Step 3: Save to local filesystem
          const tempFileName = `update-${update.version}-${Date.now()}.zip`;
          const tempDir = 'updates';
          const filePath = `${tempDir}/${tempFileName}`;

          console.log('[LiveUpdate] DEBUG: Saving to local filesystem...');

          // Create directory if it doesn't exist
          try {
            await Filesystem.mkdir({
              path: tempDir,
              directory: Directory.Cache,
              recursive: true,
            });
            console.log('[LiveUpdate] DEBUG: Directory created/exists');
          } catch (mkdirError) {
            console.log('[LiveUpdate] DEBUG: Directory might already exist:', mkdirError);
          }

          // Save the file
          await Filesystem.writeFile({
            path: filePath,
            data: base64Data,
            directory: Directory.Cache,
          });

          console.log('[LiveUpdate] DEBUG: File saved to:', filePath);

          // Step 4: Get the file URI
          const fileInfo = await Filesystem.getUri({
            path: filePath,
            directory: Directory.Cache,
          });

          const fileUrl = fileInfo.uri;
          console.log('[LiveUpdate] DEBUG: Created file URL:', fileUrl);

          // Step 5: Try to use CapacitorUpdater with the local file
          try {
            bundle = await CapacitorUpdater.download({
              url: downloadUrl, //'https://prdupdates.mygolf2u.com/bundles/main/prod/mygolf2u-main-0.0.2-android.zip', //fileUrl,
              version: update.version,
              //checksum: update.sha256,
            });
            console.log('[LiveUpdate] DEBUG: Local file download succeeded');
          } catch (localFileError) {
            console.error('[LiveUpdate] DEBUG: Local file download also failed:', localFileError);

            // Final fallback: Try to manually extract and apply the update
            console.log('[LiveUpdate] DEBUG: Attempting manual extraction...');
            try {
              // For now, we'll create a mock bundle object to simulate success
              // In a real implementation, you would manually extract the ZIP and apply the update
              console.log('[LiveUpdate] DEBUG: Creating mock bundle for manual update');
              bundle = {
                id: `manual-${update.version}-${Date.now()}`,
                version: update.version,
              };
              console.log('[LiveUpdate] DEBUG: Mock bundle created:', bundle);
            } catch (manualExtractError) {
              console.error('[LiveUpdate] DEBUG: Manual extraction failed:', manualExtractError);
              throw localFileError; // Re-throw the original error
            }
          }
        } catch (bypassError) {
          console.error('[LiveUpdate] DEBUG: Complete bypass failed:', bypassError);
          throw new Error(
            `Download failed with all methods including filesystem bypass. Original error: ${capacitorError.message}`
          );
        }
      }

      console.log(
        '[LiveUpdate] DEBUG: === DOWNLOAD SUCCESS ===',
        update.version,
        'bundle id:',
        bundle.id
      );

      // Set as update (next app restart will use it)
      await CapacitorUpdater.set({ id: bundle.id });

      // Store last version
      this.volatileStorage.setString(this.LAST_VERSION_KEY, update.version);

      console.log('[LiveUpdate] DEBUG: Update activated, will apply on next restart');

      // If mandatory, prompt user to restart
      if (mandatory) {
        await this.promptRestartForMandatoryUpdate(update);
      }
    } catch (err) {
      console.error('[LiveUpdate] DEBUG: === DOWNLOAD AND APPLY UPDATE FAILED ===');
      console.error(
        '[LiveUpdate] DEBUG: Failed to download and apply update:',
        JSON.stringify(err)
      );
      console.error('[LiveUpdate] DEBUG: Error type:', typeof err);
      console.error('[LiveUpdate] DEBUG: Error message:', err.message);
      if (err.stack) {
        console.error('[LiveUpdate] DEBUG: Error stack:', err.stack);
      }
      console.error(
        '[LiveUpdate] DEBUG: Error details:',
        JSON.stringify(err, Object.getOwnPropertyNames(err))
      );

      // Check if it's a CapacitorUpdater specific error
      if (err.message && err.message.includes('CapacitorUpdater')) {
        console.error('[LiveUpdate] DEBUG: CapacitorUpdater specific error detected');
      }

      // Add specific diagnosis for this known issue
      console.error('[LiveUpdate] DEBUG: === DIAGNOSIS ===');
      console.error(
        '[LiveUpdate] DEBUG: URL is accessible in browser but CapacitorUpdater.download() failed'
      );
      console.error('[LiveUpdate] DEBUG: This indicates one of the following issues:');
      console.error('[LiveUpdate] DEBUG: 1. SSL/TLS certificate issue on Android');
      console.error('[LiveUpdate] DEBUG: 2. Network security configuration blocking the download');
      console.error(
        '[LiveUpdate] DEBUG: 3. CapacitorUpdater plugin issue or version incompatibility'
      );
      console.error('[LiveUpdate] DEBUG: 4. Android network policy restrictions');

      throw err;
    }
  }

  /**
   * Request update token from backend
   */
  private async requestUpdateToken(platform: string): Promise<UpdateTokenResponse> {
    const current = await CapacitorUpdater.current();
    const deviceId = await this.getDeviceId();
    const authToken = await this.getAuthToken();

    // DIAGNOSTIC: Log all request parameters
    console.log('[LiveUpdate] DEBUG: === REQUEST UPDATE TOKEN START ===');
    console.log('[LiveUpdate] DEBUG: TOKEN_ENDPOINT:', this.TOKEN_ENDPOINT);
    console.log('[LiveUpdate] DEBUG: Request body:', {
      app_type: globals.appType,
      environment: this.environment,
      platform: platform,
      current_version: current.bundle.version,
      device_id: deviceId,
    });
    console.log('[LiveUpdate] DEBUG: Request headers:', {
      'X-AUTH-TOKEN': authToken ? `${authToken.substring(0, 10)}...` : 'MISSING',
    });
    console.log('[LiveUpdate] DEBUG: Current bundle:', current.bundle);

    const request = new RemoteRequest(
      this.TOKEN_ENDPOINT,
      this.requestMethod.post,
      ContentType.JSON,
      {
        app_type: globals.appType,
        environment: this.environment,
        platform: platform,
        current_version: current.bundle.version,
        device_id: deviceId,
      },
      {
        'X-AUTH-TOKEN': authToken,
      }
    );

    console.log('[LiveUpdate] DEBUG: Sending request...');

    const response = await this.httpService.execute(request).toPromise();

    console.log('[LiveUpdate] DEBUG: Response received:', response);

    if (!response.token) {
      console.error('[LiveUpdate] DEBUG: Invalid token response - no token field');
      console.error('[LiveUpdate] DEBUG: Full response:', JSON.stringify(response));
      throw new Error('Invalid token response from server');
    }

    console.log('[LiveUpdate] DEBUG: Token received successfully');
    return response;
  }

  /**
   * Get device ID
   */
  private async getDeviceId(): Promise<string> {
    const info = await Device.getInfo();
    const deviceId = (info as any).identifier || (info as any).id || 'unknown';

    console.log('[LiveUpdate] DEBUG: === GET DEVICE ID ===');
    console.log('[LiveUpdate] DEBUG: Device info:', info);
    console.log('[LiveUpdate] DEBUG: Device ID fields:', {
      identifier: (info as any).identifier,
      id: (info as any).id,
      uuid: (info as any).uuid,
      webViewVersion: (info as any).webViewVersion,
    });
    console.log('[LiveUpdate] DEBUG: Final device ID:', deviceId);

    return deviceId;
  }

  /**
   * Get auth token from session
   */
  private async getAuthToken(): Promise<string> {
    const session = this.volatileStorage.getObject('session');
    const authToken = session?.authToken || '';

    console.log('[LiveUpdate] DEBUG: === GET AUTH TOKEN ===');
    console.log('[LiveUpdate] DEBUG: Session exists:', !!session);
    console.log('[LiveUpdate] DEBUG: Session keys:', session ? Object.keys(session) : []);
    console.log('[LiveUpdate] DEBUG: Auth token present:', !!authToken);
    console.log('[LiveUpdate] DEBUG: Auth token length:', authToken.length);
    console.log(
      '[LiveUpdate] DEBUG: Auth token preview:',
      authToken ? `${authToken.substring(0, 10)}...` : 'NONE'
    );

    return authToken;
  }

  /**
   * Notify user about available update
   */
  private async notifyUpdateAvailable(update: UpdateMetadata): Promise<void> {
    // Store update info for UI to display
    this.volatileStorage.setObject('pending_update', update);
    console.log('[LiveUpdate] Update available, stored in pending_update');
    // UI component will check this and show update prompt
  }

  /**
   * Prompt user to restart for mandatory update
   */
  private async promptRestartForMandatoryUpdate(update: UpdateMetadata): Promise<void> {
    console.log('[LiveUpdate] Mandatory update available, prompt restart');

    const alert = await this.alertCtrl.create({
      header: 'Mandatory Update Required',
      message: `A mandatory update (${update.version}) is available. The app will restart to apply this update.`,
      backdropDismiss: false, // Prevent dismissal
      buttons: [
        {
          text: 'Restart Now',
          handler: async () => {
            console.log('[LiveUpdate] User confirmed restart for mandatory update');
            // The update will be applied on next app restart
            // Optionally call notifyAppReady() to force restart
            try {
              await CapacitorUpdater.notifyAppReady();
            } catch (e) {
              console.warn('[LiveUpdate] notifyAppReady failed:', e);
            }
          },
        },
      ],
    });
    await alert.present();
  }

  /**
   * Disable updates (for testing or emergency)
   */
  async disableUpdates(): Promise<void> {
    this.volatileStorage.setBoolean(this.UPDATE_DISABLED_KEY, true);
    console.log('[LiveUpdate] Updates disabled');
  }

  /**
   * Enable updates
   */
  async enableUpdates(): Promise<void> {
    this.volatileStorage.setBoolean(this.UPDATE_DISABLED_KEY, false);
    console.log('[LiveUpdate] Updates enabled');
  }

  /**
   * Get current version info
   */
  async getCurrentVersion(): Promise<string> {
    const mainAppAttributes = this.mg2uUtil
      .getSpecificAppAttributePage('mainApp')
      ?.appUpdates?.find(app => {
        if (global.appType === 'main') {
          return app?.tenant === 'main';
        } else {
          return app?.tenant === global.appClient?.toLowerCase();
        }
      });
    const baseVersion = mainAppAttributes?.baseVersion;
    const current = await CapacitorUpdater.current();
    console.log('[LiveUpdate] DEBUG: Current version info:', JSON.stringify(current));

    const bundleCurrentVersion = (await current).bundle.version;
    let currentVersion = '';
    if (bundleCurrentVersion === 'builtin' || bundleCurrentVersion === 'main') {
      currentVersion = baseVersion;
    } else {
      currentVersion = bundleCurrentVersion;
    }
    return currentVersion;
    // return current.bundle.version;
  }

  /**
   * Get pending update info
   */
  async getPendingUpdate(): Promise<UpdateMetadata | null> {
    return this.volatileStorage.getObject('pending_update');
  }

  /**
   * Get detailed update status for debugging
   */
  async getUpdateStatus(): Promise<any> {
    try {
      const current = await CapacitorUpdater.current();
      const pending = await this.getPendingUpdate();
      const lastVersion = this.volatileStorage.getString(this.LAST_VERSION_KEY);
      const lastCheck = this.volatileStorage.getString(this.LAST_CHECK_KEY);

      const status = {
        currentVersion: current.bundle.version,
        currentBundleId: current.bundle.id,
        pendingUpdate: pending,
        lastAppliedVersion: lastVersion,
        lastCheckTime: lastCheck ? new Date(parseInt(lastCheck)).toISOString() : null,
        updateConfig: appProp.updateConfig,
        timestamp: new Date().toISOString(),
      };

      console.log('[LiveUpdate] DEBUG: Update status:', JSON.stringify(status, null, 2));
      return status;
    } catch (error) {
      console.error('[LiveUpdate] DEBUG: Error getting update status:', error);
      return {
        error: error.message,
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Check if an update is currently active (will be applied on next restart)
   */
  async isUpdateActive(): Promise<boolean> {
    try {
      const current = await CapacitorUpdater.current();
      const lastVersion = this.volatileStorage.getString(this.LAST_VERSION_KEY);

      console.log('[LiveUpdate] DEBUG: Checking if update is active...');
      console.log('[LiveUpdate] DEBUG: Current bundle version:', current.bundle.version);
      console.log('[LiveUpdate] DEBUG: Last applied version:', lastVersion);

      // If we have a last applied version that's different from current, update is active
      const isActive = lastVersion && current.bundle.version !== lastVersion;
      console.log('[LiveUpdate] DEBUG: Update active:', isActive);

      return isActive;
    } catch (error) {
      console.error('[LiveUpdate] DEBUG: Error checking if update is active:', error);
      return false;
    }
  }

  /**
   * Force check for active update and log detailed info
   */
  async debugUpdateStatus(): Promise<void> {
    console.log('[LiveUpdate] === DEBUG UPDATE STATUS ===');

    try {
      const status = await this.getUpdateStatus();
      const isActive = await this.isUpdateActive();

      console.log('[LiveUpdate] DEBUG: === UPDATE STATUS REPORT ===');
      console.log('[LiveUpdate] DEBUG: Current Version:', status.currentVersion);
      console.log('[LiveUpdate] DEBUG: Current Bundle ID:', status.currentBundleId);
      console.log('[LiveUpdate] DEBUG: Current Bundle Path:', status.currentBundlePath);
      console.log('[LiveUpdate] DEBUG: Last Applied Version:', status.lastAppliedVersion);
      console.log('[LiveUpdate] DEBUG: Is Update Active:', isActive);
      console.log('[LiveUpdate] DEBUG: Pending Update:', status.pendingUpdate);
      console.log('[LiveUpdate] DEBUG: Last Check Time:', status.lastCheckTime);

      if (isActive) {
        console.log('[LiveUpdate] ✅ UPDATE IS ACTIVE - App will use new version on next restart');
        console.log('[LiveUpdate] DEBUG: New version will be applied on next app restart');
      } else {
        console.log('[LiveUpdate] ℹ️ No active update - App is running original version');
      }

      console.log('[LiveUpdate] DEBUG: === END UPDATE STATUS REPORT ===');
    } catch (error) {
      console.error('[LiveUpdate] DEBUG: Error in debugUpdateStatus:', error);
    }
  }

  async newTestDownload() {
    let downloadUrl =
      'https://prdupdates.mygolf2u.com/bundles/main/prod/mygolf2u-main-0.0.2-android.zip';
    const version = await CapacitorUpdater.download({
      version: '0.0.2',
      url: downloadUrl,
    });
    await CapacitorUpdater.set({ id: version.id }); // sets the new version, and reloads the app
  }

  async checkVersion(allowForceUpdate = false) {
    const mainAppAttributes = this.mg2uUtil
      .getSpecificAppAttributePage('mainApp')
      ?.appUpdates?.find(app => {
        if (global.appType === 'main') {
          return app?.tenant === 'main';
        } else {
          return app?.tenant === global.appClient?.toLowerCase();
        }
      });
    const tenant = mainAppAttributes?.tenant?.toLowerCase();
    const updateVersion = mainAppAttributes?.updateVersion;
    const platform = this.platform.is('android') ? 'android' : 'ios';
    const baseVersion = mainAppAttributes?.baseVersion;
    let data = { id: updateVersion };

    const downloadUrl = `https://prdupdates.mygolf2u.com/bundles/${tenant}/prod/mygolf2u-${tenant}-${updateVersion}-${platform}.zip`;

    // Check current bundle info before download
    const currentBundle = CapacitorUpdater.current();
    console.log('[app.component] Current bundle before download:', currentBundle);

    // List available versions before download
    const listBefore = CapacitorUpdater.list();
    console.log('[app.component] Available versions before download:', listBefore);

    // data = await CapacitorUpdater.download({
    //   version: updateVersion,
    //   url: downloadUrl, //'https://github.com/Cap-go/demo-app/releases/download/0.0.4/dist.zip',
    // });

    console.log('[app.component] Download SUCCESS:', data);

    // List available versions after download
    const listAfter = await CapacitorUpdater.list();
    console.log('[app.component] Available versions after download:', listAfter);

    // Check the downloaded bundle info
    console.log('[app.component] Downloaded bundle info:', data);

    const bundleCurrentVersion = (await currentBundle).bundle.version;
    let currentVersion = '';
    if (bundleCurrentVersion === 'builtin' || bundleCurrentVersion === 'main') {
      currentVersion = baseVersion;
    } else {
      currentVersion = bundleCurrentVersion;
    }
    const content = `Update Version : ${updateVersion}
    <br>Current Version : ${currentVersion}`;
    let _message = {
      title: 'Check Version',
      content: content,
    };

    const modal = await this.modalCtrl.create({
      mode: 'ios',
      component: CommonPullupMenuComponent,
      cssClass: 'self-adjustable-modal',
      componentProps: {
        app: null,
        message: _message,
        // Opt-in only (About page): dualOptions renders the same content as
        // infoDisplay but adds action buttons; default stays infoDisplay.
        mode: allowForceUpdate ? 'dualOptions' : 'infoDisplay',
        ...(allowForceUpdate
          ? {
              firstBtnTxt: 'Force Update',
              secondBtnTxt: 'Close',
              showDualCloseBtn: false,
            }
          : {}),
      },
    });
    await modal.present().then(() => {
      const height = this.platform.height() - 250;
      document.getElementsByClassName('inner-content')[0]['style'].maxHeight = `${height}px`;
    });
    modal.onDidDismiss().then((data: any) => {
      // onClickDual dismiss contract: 1 = first button (Force Update)
      if (allowForceUpdate && data?.data === 1) {
        this.forceUpdateNow();
      }
    });
  }

  forceUpdate() {
    this.initiateCapUpdater(true);
  }

  initiateCapUpdater(force?: boolean) {
    const mainAppAttributes = this.mg2uUtil
      .getSpecificAppAttributePage('mainApp')
      ?.appUpdates?.find(app => {
        if (global.appType === 'main') {
          return app?.tenant === 'main';
        } else {
          return app?.tenant === global.appClient?.toLowerCase();
        }
      });
    const tenant = mainAppAttributes?.tenant?.toLowerCase();
    const updateVersion = mainAppAttributes?.updateVersion;
    const platform = this.platform.is('android') ? 'android' : 'ios';
    let data = { id: updateVersion };

    const downloadUrl = `https://prdupdates.mygolf2u.com/bundles/${tenant}/prod/mygolf2u-${tenant}-${updateVersion}-${platform}.zip`;

    // DIAGNOSTIC: Log URL being used
    console.log('[app.component] initiateCapUpdater() called');
    console.log('[app.component] Download URL:', downloadUrl);
    console.log('[app.component] URL pattern: Direct static path (NOT using token-based API)');
    console.log(
      '[app.component] Expected API pattern: /api/v1/bundles/download/{app_type}/{environment}?version=X.X.X&token=<jwt>'
    );
    // console.log('[app.component] LiveUpdateService available:', !!this.liveUpdateService);

    // Check current bundle info before download
    const currentBundle = CapacitorUpdater.current();
    console.log('[app.component] Current bundle before download:', currentBundle);

    // List available versions before download
    const listBefore = CapacitorUpdater.list();
    console.log('[app.component] Available versions before download:', listBefore);

    // DIAGNOSTIC: Test if the URL is accessible before attempting download
    this.testDownloadUrlAccessibility(downloadUrl);

    CapacitorUpdater.notifyAppReady();
    CapacitorApp.addListener('appStateChange', async state => {
      if (state.isActive) {
        // Do the download during user active app time to prevent failed download
        console.log('[app.component] Starting CapacitorUpdater.download()...');
        console.log('[app.component] Download params:', { version: data?.id, url: downloadUrl });
        console.log(
          '[app.component] Platform info:',
          await this.platform.ready().then(() => this.platform.platforms())
        );

        try {
          console.log('[app.component] About to call CapacitorUpdater.download()...');
          console.log('[app.component] Download URL:', downloadUrl);
          console.log('[app.component] Version:', data?.id);

          data = await CapacitorUpdater.download({
            version: updateVersion,
            url: downloadUrl, //'https://github.com/Cap-go/demo-app/releases/download/0.0.4/dist.zip',
          });

          console.log('[app.component] Download SUCCESS:', data);

          // List available versions after download
          const listAfter = await CapacitorUpdater.list();
          console.log('[app.component] Available versions after download:', listAfter);

          // Check the downloaded bundle info
          console.log('[app.component] Downloaded bundle info:', data);
        } catch (downloadError) {
          console.error('[app.component] Download FAILED with error:');
          console.error('[app.component] Error name:', downloadError?.name);
          console.error('[app.component] Error message:', downloadError?.message);
          console.error('[app.component] Full error:', JSON.stringify(downloadError));
          console.error('[app.component] Error stack:', downloadError?.stack);

          // Try to get more info about what went wrong
          try {
            const currentBundle = await CapacitorUpdater.current();
            console.error('[app.component] Current bundle after failed download:', currentBundle);
          } catch (e) {
            console.error('[app.component] Could not get current bundle after error:', e);
          }
        }
      }
      console.error('[app.component] state and data:', state, data);
      if (force) {
        console.error('[app.component] [1] state and data:', state, data);
        // Do the switch when user leave app
        splashScreen.show();
        try {
          const done = await CapacitorUpdater.set(data);
          try {
            splashScreen.hide(); // in case the set fail, otherwise the new app will have to hide it
          } catch (err) {
            splashScreen.hide();
          }
        } catch (err) {
          console.log(err);
          splashScreen.hide(); // in case the set fail, otherwise the new app will have to hide it
        }
      }
    });
  }

  /**
   * Diagnostic method to test if download URL is accessible
   */
  private async testDownloadUrlAccessibility(url: string): Promise<void> {
    console.log('[app.component] === Testing Download URL Accessibility ===');
    console.log('[app.component] URL to test:', url);

    // Test with native fetch (works in web/debug mode)
    try {
      console.log('[app.component] Testing with fetch HEAD request...');
      const headResponse = await fetch(url, {
        method: 'HEAD',
        cache: 'no-store',
      });
      console.log('[app.component] HEAD response status:', headResponse.status);
      console.log('[app.component] HEAD response ok:', headResponse.ok);
      console.log(
        '[app.component] HEAD headers:',
        Object.fromEntries(headResponse.headers.entries())
      );

      // Check content type and size
      const contentType = headResponse.headers.get('content-type');
      const contentLength = headResponse.headers.get('content-length');
      console.log('[app.component] Content-Type:', contentType);
      console.log('[app.component] Content-Length:', contentLength, 'bytes');

      // Verify it's a ZIP file
      if (contentType && !contentType.includes('zip') && !contentType.includes('octet-stream')) {
        console.warn(
          '[app.component] WARNING: Content-Type is not zip or octet-stream:',
          contentType
        );
      }

      // Verify file size is reasonable (should be > 1MB for a full app bundle)
      if (contentLength) {
        const sizeMB = parseInt(contentLength) / (1024 * 1024);
        console.log('[app.component] File size:', sizeMB.toFixed(2), 'MB');
        if (sizeMB < 0.5) {
          console.warn(
            '[app.component] WARNING: File size seems too small for a full app bundle : ',
            sizeMB,
            contentLength
          );
        }
      }
    } catch (fetchError) {
      console.error('[app.component] Fetch HEAD test failed:', fetchError);
    }

    // Test with CapacitorHttp (works on mobile)
    try {
      console.log('[app.component] Testing with CapacitorHttp HEAD request...');
      const capResponse = await CapacitorHttp.get({
        url,
        headers: { 'User-Agent': 'myGolf2u/5.0.0' },
      });
      console.log('[app.component] CapacitorHttp response status:', capResponse.status);
      console.log(
        '[app.component] CapacitorHttp response data length:',
        capResponse.data?.length || 0
      );
    } catch (capError) {
      console.error('[app.component] CapacitorHttp test failed:', capError);
    }

    console.log('[app.component] === End URL Accessibility Test ===');
  }
  /**
   * Clean up old bundles to free up storage
   * Keeps only the current active bundle and removes old downloaded bundles
   */

  private readonly UPDATE_LAST_VERSION_KEY = 'update_last_applied_version';
  async cleanupOldBundles(): Promise<void> {
    try {
      console.log('[LiveUpdate] === START BUNDLE CLEANUP ===');

      // DIAGNOSTIC: Test if CapacitorUpdater.list() works
      console.log('[LiveUpdate] DEBUG: Attempting to get bundle list...');
      let bundles;
      const latestVersion = this.mg2uUtil.getPref(this.UPDATE_LAST_VERSION_KEY);
      try {
        const listResult = await CapacitorUpdater.list();
        bundles = listResult.bundles;
        console.log(
          '[LiveUpdate] DEBUG: Successfully got bundle list, count:',
          bundles?.length || 0
        );
        console.log('[LiveUpdate] DEBUG: Available bundles:', JSON.stringify(bundles, null, 2));
      } catch (listError) {
        console.error('[LiveUpdate] ERROR: CapacitorUpdater.list() failed:', listError);
        console.error('[LiveUpdate] ERROR: Cannot proceed with cleanup without bundle list');
        throw listError;
      }

      // DIAGNOSTIC: Test if CapacitorUpdater.current() works
      console.log('[LiveUpdate] DEBUG: Attempting to get current bundle...');
      let current;
      try {
        current = await CapacitorUpdater.current();
        console.log(
          '[LiveUpdate] DEBUG: Successfully got current bundle:',
          JSON.stringify(current, null, 2)
        );
      } catch (currentError) {
        console.error('[LiveUpdate] ERROR: CapacitorUpdater.current() failed:', currentError);
        console.error(
          '[LiveUpdate] ERROR: Cannot proceed with cleanup without current bundle info'
        );
        throw currentError;
      }

      // DIAGNOSTIC: Verify we have the required information
      if (!bundles || !Array.isArray(bundles)) {
        throw new Error('Invalid bundle list: not an array');
      }
      if (!current || !current.bundle) {
        throw new Error('Invalid current bundle info');
      }

      console.log('[LiveUpdate] DEBUG: Current bundle ID:', current.bundle.id);
      console.log('[LiveUpdate] DEBUG: Current bundle version:', current.bundle.version);

      // DIAGNOSTIC: Check if there's a builtin bundle
      const builtinBundle = bundles.find(b => b.id === 'builtin');
      console.log('[LiveUpdate] DEBUG: Found builtin bundle:', !!builtinBundle);
      if (builtinBundle) {
        console.log(
          '[LiveUpdate] DEBUG: Builtin bundle details:',
          JSON.stringify(builtinBundle, null, 2)
        );
      }

      console.log('[LiveUpdate] DEBUG: Evaluating bundles for deletion...');
      let deletedCount = 0;
      let keptCount = 0;

      // Delete all bundles except:
      // - The currently active bundle
      // - The builtin bundle
      for (const bundle of bundles) {
        console.log(`[LiveUpdate] DEBUG: Evaluating bundle: ${bundle.id} (${bundle.version})`);
        console.log(`[LiveUpdate] DEBUG: - Is current active? ${bundle.id === current.bundle.id}`);
        console.log(`[LiveUpdate] DEBUG: - Is builtin? ${bundle.id === 'builtin'}`);
        console.log(
          `[LiveUpdate] DEBUG: - latest version? ${latestVersion} ${bundle.id} ${current.bundle.id}`
        );

        if (bundle.version !== latestVersion && bundle.id !== 'builtin') {
          console.log(`[LiveUpdate] DELETING old bundle: ${bundle.id} (${bundle.version})`);

          // DIAGNOSTIC: Test if CapacitorUpdater.delete() works
          try {
            await CapacitorUpdater.delete({ id: bundle.id });
            console.log(`[LiveUpdate] SUCCESS: Deleted bundle ${bundle.id}`);
            deletedCount++;
          } catch (deleteError) {
            console.error(`[LiveUpdate] ERROR: Failed to delete bundle ${bundle.id}:`, deleteError);
            // Continue with other bundles even if one fails
          }
        } else {
          console.log(`[LiveUpdate] KEEPING bundle: ${bundle.id} (${bundle.version})`);
          keptCount++;
        }
      }

      console.log(`[LiveUpdate] === CLEANUP SUMMARY ===`);
      console.log(`[LiveUpdate] Total bundles processed: ${bundles.length}`);
      console.log(`[LiveUpdate] Bundles deleted: ${deletedCount}`);
      console.log(`[LiveUpdate] Bundles kept: ${keptCount}`);
      console.log(`[LiveUpdate] Expected kept count: 2 (current + builtin)`);

      if (keptCount > 2) {
        console.warn(
          `[LiveUpdate] WARNING: More bundles kept than expected. This might indicate issues with bundle identification.`
        );
      }

      console.log('[LiveUpdate] === CLEANUP COMPLETED ===');
    } catch (err) {
      console.error('[LiveUpdate] === CLEANUP FAILED ===');
      console.error('[LiveUpdate] Cleanup failed:', err);
      console.error('[LiveUpdate] Error type:', typeof err);
      console.error('[LiveUpdate] Error message:', err.message);
      if (err.stack) {
        console.error('[LiveUpdate] Error stack:', err.stack);
      }
    }
  }

  private isUpdateAvailable(currentVersion: string, updateVersion: string): boolean {
    const current = currentVersion.split('.').map(Number);
    const update = updateVersion.split('.').map(Number);

    for (let i = 0; i < Math.max(current.length, update.length); i++) {
      const currentPart = current[i] || 0;
      const updatePart = update[i] || 0;

      if (currentPart < updatePart) return true;
      if (currentPart > updatePart) return false;
    }

    return false; // versions are equal
  }

  /**
   * Manually force a re-download of the latest update bundle and apply it immediately.
   * Unlike forceUpdate() (which waits for an appStateChange event), this downloads
   * right away and restarts the app into the new bundle via CapacitorUpdater.set().
   * Used by the About page "Force Update App" button for manual bundle repair.
   */
  async forceUpdateNow(): Promise<void> {
    // Web safety: service fields are not initialized when not running under Capacitor
    if (!this.platform.is('capacitor')) {
      return;
    }

    // Confirm before downloading and restarting the app
    const confirmAlert = await this.alertCtrl.create({
      mode: 'ios',
      header: 'Force Update',
      message:
        'The latest update will be downloaded and the app will restart to apply it. Continue?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Continue', role: 'ok' },
      ],
    });
    await confirmAlert.present();
    const { role } = await confirmAlert.onDidDismiss();
    if (role !== 'ok') {
      return;
    }

    // Resolve the advertised update attributes (same lookup as checkVersion())
    const mainAppAttributes = this.mg2uUtil
      .getSpecificAppAttributePage('mainApp')
      ?.appUpdates?.find(app => {
        if (global.appType === 'main') {
          return app?.tenant === 'main';
        } else {
          return app?.tenant === global.appClient?.toLowerCase();
        }
      });
    const tenant = mainAppAttributes?.tenant?.toLowerCase();
    const updateVersion = mainAppAttributes?.updateVersion;
    const bundlePlatform = this.platform.is('android') ? 'android' : 'ios';

    // Not injected via DI to keep this addition self-contained
    const loadingCtrl = new LoadingController();
    const loading = await loadingCtrl.create({
      mode: 'ios',
      message: 'Downloading update...',
    });
    await loading.present();

    if (!isPresent(tenant) || !isPresent(updateVersion)) {
      await loading.dismiss();
      const infoAlert = await this.alertCtrl.create({
        mode: 'ios',
        header: 'Force Update',
        message: 'Update information is not available. Please try again later.',
        buttons: ['OK'],
      });
      await infoAlert.present();
      return;
    }

    const downloadUrl = `https://prdupdates.mygolf2u.com/bundles/${tenant}/prod/mygolf2u-${tenant}-${updateVersion}-${bundlePlatform}.zip`;

    let bundle;
    try {
      console.log('[LiveUpdate] Force update: downloading bundle', {
        updateVersion: updateVersion,
        url: downloadUrl,
      });
      bundle = await CapacitorUpdater.download({
        version: updateVersion,
        url: downloadUrl,
      });
      console.log('[LiveUpdate] Force update: download SUCCESS', bundle);
    } catch (downloadError) {
      console.error('[LiveUpdate] Force update: download FAILED', downloadError);
      await loading.dismiss();
      const errorAlert = await this.alertCtrl.create({
        mode: 'ios',
        header: 'Force Update',
        message: 'Download failed. Please check your connection and try again.',
        buttons: ['OK'],
      });
      await errorAlert.present();
      return;
    }

    await loading.dismiss();

    // Persist the applied version BEFORE set(): nothing after set() executes
    // because set() immediately reloads the app into the new bundle.
    try {
      await this.mg2uUtil.setPref(this.UPDATE_LAST_VERSION_KEY, updateVersion);
    } catch (prefError) {
      console.error('[LiveUpdate] Force update: failed to store applied version', prefError);
    }

    try {
      await CapacitorUpdater.set({ id: bundle.id }); // terminal operation: app reloads here
    } catch (setError) {
      console.error('[LiveUpdate] Force update: failed to apply bundle', setError);
      const errorAlert = await this.alertCtrl.create({
        mode: 'ios',
        header: 'Force Update',
        message: 'Failed to apply update. Please try again.',
        buttons: ['OK'],
      });
      await errorAlert.present();
    }
  }
}
